import React, { useEffect, useState, useCallback } from 'react';
import { OtService } from '../services/OtService';
import { ModalEvidenciasCierre } from './ModalEvidenciasCierre';
import { ExpedienteEvidenciasPorConcepto } from './ExpedienteEvidenciasPorConcepto';
import { FileUploaderPreview } from './FileUploaderPreview';
import { OrdenCompraDownloadButton } from './OrdenCompraDownloadButton';
import { DetalleOT, EvidenciaImg } from '../types/ot.types';
import { countEvidenciasCierre } from '../utils/otDateValidation';
import {
    CONTEXTO_EVIDENCIA_CIERRE,
    CONTEXTO_REPARACION,
    parseEvidenciasReparacionPorConcepto,
    countEvidenciasReparacion,
} from '../utils/otEvidenciasExpediente';
import { mapDetalleExpedienteToOT } from '../utils/otDetalleExpediente';

/** Tipo archivo OC en catálogo (Archivo::TIPO_ORDEN_COMPRA = 11) */
const TIPO_ARCHIVO_ORDEN_COMPRA = 11;

const badgeAutorizacion = (valor: any, labelPendiente = 'Pendiente') => {
    if (valor == 1) {
        return <span className="text-success small fw-bold"><i className="fas fa-check-circle"></i> Aprobado</span>;
    }
    if (valor == 0) {
        return <span className="text-danger small fw-bold"><i className="fas fa-times-circle"></i> Rechazado</span>;
    }
    return <span className="text-warning small fw-bold"><i className="fas fa-clock"></i> {labelPendiente}</span>;
};

export const ExpedienteOT: React.FC<{ ordenId: string, solicitudId: string }> = ({ ordenId, solicitudId }) => {
    const [expediente, setExpediente] = useState<any>(null);
    const [evidenciasCierre, setEvidenciasCierre] = useState<Record<number, EvidenciaImg[]>>({});
    const [showModal, setShowModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [alertMsg, setAlertMsg] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

    const getStepLabel = (keyLabel: string): string => {
        const mapper: Record<string, string> = {
            "STEP_AGENDAR": "1. Agendar cita", "STEP_INGRESO": "2. Ingreso bien",
            "STEP_DIAGNOSTICO": "3. Diagnóstico",
            "STEP_APROB_CLIENTE": "5. Aprobación Cliente", "STEP_APROB_ADMIN": "6. Aprobación Admin",
            "STEP_VERIFICACION_PAGO": "7. Verificación Pago", "STEP_ENTREGA": "8. Entrega y Cierre",
        };
        return mapper[keyLabel] ?? "Otros";
    };

    const stepsOrder = [
        "STEP_AGENDAR", "STEP_INGRESO", "STEP_DIAGNOSTICO",
        "STEP_APROB_CLIENTE", "STEP_APROB_ADMIN", "STEP_VERIFICACION_PAGO", "STEP_ENTREGA"
    ];

    const loadExpediente = useCallback(async () => {
        const res = await OtService.getExpediente(ordenId);
        setExpediente(res.data);
        const archivosCierre = (res.data?.archivos || []).filter((a: any) => a.contexto === CONTEXTO_EVIDENCIA_CIERRE);
        const dict: Record<number, EvidenciaImg[]> = {};
        archivosCierre.forEach((a: any) => {
            const detalleId = Number.parseInt(a.referencia_contexto);
            if (!Number.isNaN(detalleId)) {
                if (!dict[detalleId]) dict[detalleId] = [];
                dict[detalleId].push(a);
            }
        });
        setEvidenciasCierre(dict);
    }, [ordenId]);

    useEffect(() => {
        loadExpediente();
    }, [loadExpediente]);

    const mapDetallesToOT = (detalles: any[]): DetalleOT[] =>
        detalles.map((d: any) => mapDetalleExpedienteToOT(d));

    const handleSaveComplementaria = async () => {
        const nuevosArchivos = Object.values(evidenciasCierre).some((arr) =>
            arr.some((a) => !!a.file)
        );
        if (!nuevosArchivos) {
            setAlertMsg({ type: 'danger', message: 'Debe seleccionar al menos un archivo nuevo para anexar.' });
            return;
        }
        setIsSaving(true);
        setAlertMsg(null);
        try {
            await OtService.saveEvidenciasCierreComplementaria(ordenId, evidenciasCierre);
            setShowModal(false);
            await loadExpediente();
            setAlertMsg({ type: 'success', message: 'Evidencia complementaria registrada correctamente.' });
        } catch (error: any) {
            setAlertMsg({ type: 'danger', message: error.message || 'Error al guardar evidencia complementaria.' });
        } finally {
            setIsSaving(false);
        }
    };

    if (!expediente) return <div className="text-center p-5"><div className="spinner-border"></div></div>;

    const { orden, proveedor, centro, detalles, seguimientos, archivos } = expediente;
    const isClosed = !!(orden.fecha_entrega_bien || orden.fecha_cierre);
    const totalCierre = countEvidenciasCierre(evidenciasCierre);
    const detallesOT = mapDetallesToOT(detalles);

    const evidenciasReparacion = parseEvidenciasReparacionPorConcepto(archivos || []);
    const totalesReparacion = countEvidenciasReparacion(
        evidenciasReparacion.inicio,
        evidenciasReparacion.cierre,
    );

    const totalCotizado = detalles.reduce((acc: number, d: any) => acc + ((Number(d.precio_unitario) + Number(d.iva || 0) - Number(d.isr || 0)) * Number(d.cantidad)), 0);
    const totalAutorizado = detalles.filter((d: any) => d.autorizado == 1).reduce((acc: number, d: any) => acc + ((Number(d.precio_unitario) + Number(d.iva || 0) - Number(d.isr || 0)) * Number(d.cantidad)), 0);

    const bitacoraOrdenada = [...seguimientos].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());

    const esArchivoOrdenCompra = (file: any) => {
        if (Number(file.clave_tipo_archivo) === TIPO_ARCHIVO_ORDEN_COMPRA) return true;
        const nombre = String(file.nombreOriginal || file.nombreSistema || '').toLowerCase();
        return nombre.includes('orden-compra') || nombre.includes('orden_compra');
    };

    const archivosPorContexto = (archivos || []).reduce((acc: any, file: any) => {
        if ((CONTEXTO_REPARACION as readonly string[]).includes(file.contexto)) {
            return acc;
        }
        if (esArchivoOrdenCompra(file)) {
            return acc;
        }
        (acc[file.contexto] = acc[file.contexto] || []).push(file);
        return acc;
    }, {});

    const ocPdfUrl = orden.orden_compra_pdf_url || null;
    const ocVersiones: any[] = Array.isArray(orden.orden_compra_versiones) ? orden.orden_compra_versiones : [];
    const tieneOc = !!ocPdfUrl || ocVersiones.length > 0;

    return (
        <div className="container-fluid py-3">
            {alertMsg && (
                <div className={`alert alert-${alertMsg.type} shadow-sm`}>{alertMsg.message}</div>
            )}

            <div className="d-flex justify-content-between mb-4 border-bottom pb-3">
                <div>
                    <h2>Expediente: {orden.folio} {orden.clave_estatus}</h2>
                    <div>
                        Pagado: {orden.is_pagado == 1 && <span className="badge bg-success">Si</span>}
                        {orden.is_pagado == 0 && <span className="badge bg-secondary">No</span>}
                        <br />
                        Facturado: {orden.is_facturado == 1 && <span className="badge bg-success">Si</span>}
                        {orden.is_facturado == 0 && <span className="badge bg-secondary">No</span>}
                    </div>
                </div>
                <div>
                    <button className="btn btn-outline-primary" onClick={() => window.print()}><i className="fas fa-print"></i> Exportar Reporte</button>
                </div>
            </div>

            <div className="card mb-4 border-0 shadow-sm">
                <div className="card-header bg-dark text-white">Datos de Operación</div>
                <div className="card-body">
                    <div className="row small">
                        <div className="col-md-3"><strong>Taller:</strong><br />{proveedor?.nombre_completo} - {centro?.sucursal}</div>
                        <div className="col-md-3"><strong>Bien:</strong><br />{expediente.bien.nombre_bien} ({expediente.bien.numero_serie})</div>
                        <div className="col-md-3"><strong>Kilometraje:</strong><br />{Number(orden.kilometraje).toLocaleString()} km</div>
                        <div className="col-md-3"><strong>Fecha Alta:</strong><br />{orden.fecha_alta}</div>
                    </div>
                    <hr />
                    <div className="row small">
                        <div className="col-md-3"><strong>Fecha Cita:</strong><br />{expediente.citas[0]?.fecha_cita || '---'}</div>
                        <div className="col-md-3"><strong>Fecha Ingreso:</strong><br />{orden.fecha_recepcion_bien || '---'}</div>
                        <div className="col-md-3"><strong>Fecha Terminación:</strong><br />{orden.fecha_terminacion || '---'}</div>
                        <div className="col-md-3"><strong>Fecha Entrega:</strong><br />{orden.fecha_entrega_bien || '---'}</div>
                    </div>
                </div>
            </div>

            {tieneOc && (
                <div className="card mb-4 border-0 shadow-sm border-start border-danger border-4">
                    <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <span className="fw-bold">
                            <i className="fas fa-file-invoice text-danger me-2"></i>
                            Orden de Compra
                        </span>
                        <OrdenCompraDownloadButton
                            ordenId={ordenId}
                            pdfUrl={ocPdfUrl}
                            visible={!!ocPdfUrl}
                            id="expediente-orden-compra-btn"
                        />
                    </div>
                    <div className="card-body">
                        <p className="small text-muted mb-2">
                            Documento vigente generado al completar la aprobación de servicio. Consultable en cualquier momento (no forma parte de las evidencias por paso).
                        </p>
                        {ocVersiones.length > 1 && (
                            <div className="table-responsive">
                                <table className="table table-sm table-hover mb-0 align-middle">
                                    <thead>
                                        <tr>
                                            <th>Versión</th>
                                            <th>Fecha</th>
                                            <th>Motivo</th>
                                            <th className="text-end">Monto</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ocVersiones.map((v) => (
                                            <tr key={v.clave_version} className={v.es_vigente == 1 ? 'table-success' : ''}>
                                                <td>
                                                    v{v.numero_version}
                                                    {v.es_vigente == 1 && <span className="badge bg-success ms-2">Vigente</span>}
                                                </td>
                                                <td className="small">{v.fecha_alta || '—'}</td>
                                                <td className="small">{v.motivo || '—'}</td>
                                                <td className="text-end">${Number(v.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                                                <td className="text-end">
                                                    {v.pdf_url ? (
                                                        <a href={v.pdf_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-danger">
                                                            <i className="fas fa-file-pdf"></i>
                                                        </a>
                                                    ) : (
                                                        <span className="text-muted small">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {isClosed && (
                <div className="card mb-4 border-0 shadow-sm">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <span className="fw-bold">Evidencias de cierre de reparación</span>
                        <button
                            type="button"
                            className={`btn btn-sm ${totalCierre === 0 ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                            onClick={() => setShowModal(true)}
                        >
                            <i className="fas fa-camera me-1"></i>
                            Anexar evidencia complementaria
                        </button>
                    </div>
                    <div className="card-body">
                        {totalCierre === 0 ? (
                            <span className="badge bg-danger">Sin evidencia de cierre de reparación registrada</span>
                        ) : (
                            <span className="badge bg-success mb-2">{totalCierre} archivo(s) de cierre</span>
                        )}
                    </div>
                </div>
            )}

            <ModalEvidenciasCierre
                show={showModal}
                onClose={() => setShowModal(false)}
                onSave={handleSaveComplementaria}
                isSaving={isSaving}
                detallesOT={detallesOT}
                evidenciasCierre={evidenciasCierre}
                setEvidenciasCierre={setEvidenciasCierre}
                title="Anexar evidencia de cierre post-operativo"
                saveLabel="Guardar evidencia complementaria"
            />

            <div className="row mb-4">
                <div className="col-md-6"><div className="card p-3 border-start border-primary border-4"><strong>Total Cotizado:</strong> <span className="h4">${totalCotizado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span></div></div>
                <div className="col-md-6"><div className="card p-3 border-start border-success border-4"><strong>Total Autorizado:</strong> <span className="h4">${totalAutorizado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span></div></div>
            </div>

            <h4 className="mt-4">Conceptos y Desglose Financiero</h4>
            <table className="table table-bordered table-sm mb-4">
                <thead className="table-light">
                    <tr>
                        <th>Categoría / Subcategoría / Detalle</th>
                        <th>Cant</th>
                        <th>P. Unit</th>
                        <th>IVA</th>
                        <th>ISR</th>
                        <th>Total</th>
                        <th>Aprob. servicio</th>
                        <th>Aprob. admin</th>
                    </tr>
                </thead>
                <tbody>
                    {detalles.map((d: any, i: number) => (
                        <tr key={i}>
                            <td>
                                <div><strong>{d.categoria}</strong> / {d.subcategoria}</div>
                                <div className="small text-muted">{d.titulo}</div>
                                {d.observaciones && <div className="small fst-italic text-secondary">Observaciones: {d.observaciones}</div>}
                            </td>
                            <td>{d.cantidad}</td>
                            <td>${Number(d.precio_unitario).toFixed(2)}</td>
                            <td>${Number(d.iva || 0).toFixed(2)}</td>
                            <td>${Number(d.isr || 0).toFixed(2)}</td>
                            <td>${((Number(d.precio_unitario) + Number(d.iva || 0) - Number(d.isr || 0)) * Number(d.cantidad)).toFixed(2)}</td>
                            <td>{badgeAutorizacion(d.autorizado)}</td>
                            <td>{badgeAutorizacion(d.clave_aprobacion)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <h4 className="mt-4">Evidencias de reparación por concepto</h4>
            <p className="text-muted small">
                Cada concepto muestra por separado las evidencias de <strong>inicio</strong> y de <strong>cierre</strong> de reparación para facilitar el análisis.
            </p>
            <div className="d-flex flex-wrap gap-2 mb-3">
                <span className="badge bg-primary">
                    Inicio: {totalesReparacion.inicio} archivo(s)
                </span>
                <span className="badge bg-success">
                    Cierre: {totalesReparacion.cierre} archivo(s)
                </span>
            </div>
            <ExpedienteEvidenciasPorConcepto
                detallesOT={detallesOT}
                evidenciasInicio={evidenciasReparacion.inicio}
                evidenciasCierre={evidenciasReparacion.cierre}
            />

            <h4 className="mt-4">Expediente Documental (Otros pasos)</h4>
            <div className="row">
                {stepsOrder.filter(step => archivosPorContexto[step]?.length > 0).map((step) => (
                    <div key={step} className="col-md-6 mb-3">
                        <div className="card h-100 shadow-sm border-0">
                            <div className="card-header bg-secondary text-white small fw-bold text-uppercase">{getStepLabel(step)}</div>
                            <div className="card-body p-2">
                                <FileUploaderPreview
                                    label=""
                                    archivos={archivosPorContexto[step]}
                                    setArchivos={() => { }}
                                    multiple={true}
                                    disabled={true}
                                />
                            </div>
                        </div>
                    </div>
                ))}
                {stepsOrder.every(step => !archivosPorContexto[step]?.length) && (
                    <div className="col-12">
                        <div className="alert alert-secondary mb-0">
                            No hay otros archivos documentales registrados fuera de las evidencias de reparación.
                        </div>
                    </div>
                )}
            </div>

            <h4 className="mt-4 border-top pt-3">Bitácora de Seguimiento (Historial)</h4>
            <div className="card shadow-sm">
                <div className="card-body p-0">
                    <ul className="list-group list-group-flush">
                        {bitacoraOrdenada.map((s: any, i: number) => (
                            <li key={i} className="list-group-item d-flex">
                                <div className="me-3 text-center" style={{ minWidth: '100px' }}>
                                    <span className="badge bg-light text-dark border">{s.fecha.split(' ')[0]}</span><br />
                                    <small className="text-muted">{s.fecha.split(' ')[1]}</small>
                                </div>
                                <div>
                                    <strong>{s.usuario}</strong> <span className="text-muted small text-uppercase">[{s.descripcion}]</span>
                                    <p className="mb-0 mt-1 small">{s.comentarios}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};
export default ExpedienteOT;
